import { Injectable } from '@angular/core';
import { TsTableService } from './ts-table.service';

@Injectable({
  providedIn: 'root',
})
export class TsTableGroupedService extends TsTableService {
  public flattenDataWithHierarchyAndGrouping<T>(
    data: T[],
    groups: any[] = [],
    getId: (item: T) => string,
    getParentId: (item: T) => string | null,
    groupByField: string | string[] | null,
    showUncategorized: boolean = true,
  ): any[] {
    const flatTree: any[] = [];
    const categorizedItems = new Set<string>();
    const getGroupFieldValues = (item: T): string => {
      if (Array.isArray(groupByField)) {
        const values = groupByField
          .map((field) => this.getNestedValue(item, field))
          .reduce((acc, val) => acc.concat(val), [])
          .filter(Boolean);
        return values.join(',');
      } else if (typeof groupByField === 'string') {
        const fieldValue = this.getNestedValue(item, groupByField);
        return Array.isArray(fieldValue) ? fieldValue.join(',') : fieldValue || '';
      }
      return '';
    };

    const sortedGroups = [
      ...groups.filter((group) => group.label !== 'Other'),
      ...groups.filter(
        (group, index, arr) => group.label === 'Other' && arr.findIndex((g) => g.label === 'Other') === index,
      ),
    ];

    if (sortedGroups.length > 0 && groupByField) {
      sortedGroups.forEach((group) => {
        flatTree.push({
          ...group,
          isCategory: true,
          path: [group.label],
          depth: 0,
        });

        const groupedItems = data.filter((item) => {
          const groupFieldValues = getGroupFieldValues(item);
          if (typeof groupFieldValues === 'object' && groupFieldValues !== null) {
            return Object.values(groupFieldValues).includes(group.label);
          }
          return groupFieldValues.split('|')[1] === group.label || groupFieldValues === group.label;
        });

        groupedItems.forEach((item) => {
          const path = [group.label, getId(item)];
          if (!getParentId(item)) {
            this.addItemToTree(flatTree, item, path, getId, getParentId);
            categorizedItems.add(getId(item));
          }
        });
      });
    }

    if (showUncategorized || sortedGroups.length === 0 || !groupByField) {
      const uncategorizedItems = data.filter((item) => !categorizedItems.has(getId(item)) || sortedGroups.length === 0);
      uncategorizedItems.forEach((item) => {
        if (!getParentId(item)) {
          const uncategorizedGroup = sortedGroups.find((group) => group.label === 'Other');
          const path = uncategorizedGroup ? [uncategorizedGroup.label, getId(item)] : [getId(item)];
          this.addItemToTree(flatTree, item, path, getId, getParentId);
        }
      });
    }

    return flatTree;
  }

  addItemToTree<T>(
    tree: any[],
    item: T,
    path: string[],
    getId: (item: T) => string,
    getParentId: (item: T) => string | null,
  ) {
    const hasChildren = Array.isArray(item['children']) && item['children'].length > 0;

    const node = {
      ...item,
      path: path,
      depth: path.length - 1,
      isParent: hasChildren,
    };

    tree.push(node);
    if (hasChildren) {
      item['children'].forEach((child) => {
        const childPath = [...path, getId(child)];
        this.addItemToTree(tree, child, childPath, getId, getParentId);
      });
    }
  }

  getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }
}
